import React, { useContext, useState, useEffect } from 'react';
import { Search, AlertCircle, Loader2, Eye, Filter, X, ChevronDown, Activity, Building2, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../../../context/AuthContext';
import superAdminServices from '../../../services/superAdminServices';
import Pagination from '../../../components/Pagination';

export default function AllActivities() {
  const navigate = useNavigate();
  const { authTokens } = useContext(AuthContext);

  const [showFiltersSection, setShowFiltersSection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activities, setActivities] = useState([]);
  const [pagination, setPagination] = useState({
    count: 0,
    page: 1,
    page_size: 25,
    total_pages: 0
  });

  const [filters, setFilters] = useState({
    activityName: '',
    organization: '',
    status: '',
    sortBy: 'newest'
  });

  // Fetch activities on mount and when pagination changes
  useEffect(() => {
    fetchActivities();
  }, [pagination.page, pagination.page_size]);

  const fetchActivities = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.page_size,
      };

      // Add filters
      if (filters.activityName) params.activity_name = filters.activityName;
      if (filters.organization) params.organization = filters.organization;
      if (filters.status) params.status = filters.status;
      if (filters.sortBy) params.ordering = mapSortToOrdering(filters.sortBy);

      const response = await superAdminServices.getAllActivities(authTokens.access, params);

      setActivities(response.results || []);
      setPagination(prev => ({
        ...prev,
        count: response.count,
        total_pages: response.total_pages
      }));
    } catch (err) {
      setError(err.message);
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  const mapSortToOrdering = (sortBy) => {
    const sortMap = {
      'activity_name': 'activity_name',
      'organization': 'organisation__organisation_name',
      'status': 'status',
      'newest': '-created_at',
      'oldest': 'created_at'
    };
    return sortMap[sortBy] || '-created_at';
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
    fetchActivities();
  };

  const handleClearFilters = () => {
    setFilters({
      activityName: '',
      organization: '',
      status: '',
      sortBy: 'newest'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    // Fetch will be triggered by useEffect when filters change
    setTimeout(() => fetchActivities(), 0);
  };

  const handleShowFilters = () => {
    setShowFiltersSection(!showFiltersSection);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination(prev => ({ 
      ...prev, 
      page_size: newPageSize, 
      page: 1 // Reset to first page when changing page size
    }));
  };

  const handleViewActivity = (activityId) => {
    navigate(`/admin-panel/activities/${activityId}`);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'Active': 'bg-green-100 text-green-700',
      'Inactive': 'bg-red-100 text-red-700',
      'Pending': 'bg-yellow-100 text-yellow-700',
      'Completed': 'bg-blue-100 text-blue-700'
    };

    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-700';

    return (
      <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${colorClass}`}>
        {status}
      </span>
    );
  };

  return (
    <div className="p-5 sm:p-6 max-w-7xl mx-auto w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Toutes les activités</h2>
          <p className="text-xs text-gray-500 mt-1">Gérez toutes les activités du système</p>
        </div>
        
        <button
          onClick={handleShowFilters}
          className="inline-flex items-center px-3 py-2 border border-gray-200 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
        >
          <Filter className="h-4 w-4 mr-2" />
          Filtres
          <ChevronDown className={`h-4 w-4 ml-1 transition-transform ${showFiltersSection ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Filters Section */}
      {showFiltersSection && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Activity Name Search */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Nom de l'activité
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher une activité..."
                  value={filters.activityName}
                  onChange={(e) => handleFilterChange('activityName', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Organization */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Organisation
              </label>
              <input
                type="text"
                placeholder="Nom de l'organisation"
                value={filters.organization}
                onChange={(e) => handleFilterChange('organization', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Status */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Statut
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Tous les statuts</option>
                <option value="Active">Actif</option>
                <option value="Inactive">Inactif</option>
                <option value="Pending">En attente</option>
                <option value="Completed">Terminé</option>
              </select>
            </div>

            {/* Sort */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Trier par
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full px-3 py-2 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="newest">Plus récent</option>
                <option value="oldest">Plus ancien</option>
                <option value="activity_name">Nom de l'activité (A-Z)</option>
                <option value="organization">Organisation (A-Z)</option>
                <option value="status">Statut</option>
              </select>
            </div>
          </div>

          {/* Filter Actions */}
          <div className="flex items-center justify-end space-x-3 mt-4">
            <button
              onClick={handleClearFilters}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              <X className="h-4 w-4 mr-1" />
              Effacer
            </button>
            <button
              onClick={handleApplyFilters}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              Appliquer les filtres
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-sm text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Activities Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Activité</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Organisation</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Statut</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Risques</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Créé par</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700">Créé le</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
                      <span className="ml-2 text-sm text-gray-600">Chargement...</span>
                    </div>
                  </td>
                </tr>
              ) : activities.length > 0 ? (
                activities.map((activity) => (
                  <tr
                    key={activity.id}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-gradient-to-r from-black to-gray-900 rounded-full flex items-center justify-center mr-3">
                          <Activity className="h-4 w-4 text-green-500" />
                        </div>
                        <div>
                          <p className="text-xs font-medium text-gray-900">{activity.activity_name}</p>
                          {activity.description && (
                            <p className="text-xs text-gray-500 truncate max-w-xs">
                              {activity.description.length > 50 
                                ? `${activity.description.substring(0, 50)}...` 
                                : activity.description
                              }
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <Building2 className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-900">
                          {activity.organization?.name || 'Aucune organisation'}
                        </span>
                        {activity.organization?.is_subscribed && (
                          <span className="ml-2 inline-block w-2 h-2 bg-green-400 rounded-full" title="Abonné"></span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(activity.status)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-gray-600">
                        {activity.risks_count} risque(s)
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center">
                        <User className="h-3 w-3 text-gray-400 mr-1" />
                        <span className="text-xs text-gray-900">
                          {activity.created_by?.name || activity.created_by?.email || 'Inconnu'}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">
                      {new Date(activity.created_at).toLocaleDateString('fr-FR')}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleViewActivity(activity.id)}
                        className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        title="Voir les détails"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center">
                    <div className="flex flex-col items-center">
                      <Activity className="h-12 w-12 text-gray-300 mb-3" />
                      <p className="text-xs text-gray-500">Aucune activité trouvée</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={pagination.page}
          totalPages={pagination.total_pages}
          totalItems={pagination.count}
          pageSize={pagination.page_size}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          loading={loading}
          itemName="activités"
        />
      </div>
    </div>
  );
}
